import { databases } from '../lib/appwrite';
import { ID, Permission, Role, Query } from 'appwrite';

// ============================================
// APPWRITE CASHOUT SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const CASHOUT_COLLECTION_ID = process.env.REACT_APP_APPWRITE_CASHOUT_COLLECTION_ID;

/**
 * Get the most recent cashout request for a user
 */
export const getLastCashout = async (userId) => {
  if (!userId) return null;
  try {
    const res = await databases.listDocuments(DATABASE_ID, CASHOUT_COLLECTION_ID, [
      Query.equal('user_id', userId),
      Query.orderDesc('$createdAt'),
      Query.limit(1)
    ]);
    return res.documents[0] || null;
  } catch (error) {
    console.error('getLastCashout error', error);
    return null;
  }
};

/**
 * Calculate fee percentage based on time since last cashout
 * @param {Date|string|null} lastDate 
 * @returns {number} fee percentage (0.01 = 1%)
 */
export const calculateCooldownFee = (lastDate) => {
  if (!lastDate) return 0.01; // First time: 1%

  const last = new Date(lastDate);
  const now = new Date();
  const diffTime = Math.max(0, now - last);
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  if (diffDays < 14) return 0.03;      // < 2 weeks: 3%
  if (diffDays < 30) return 0.02;      // 2-4 weeks: 2%
  return 0.01;                        // > 1 month: 1%
};



/**
 * Create a cashout request document
 * @param {string} userId - Appwrite user id
 * @param {number} amountRequested - Amount in RM requested by user
 * @param {Object} bankInfo - { bank_name, account_number, account_holder_name }
 * @param {number} [adminFeePercentage] - Optional admin fee override (percentage)
 * @returns {Promise<Object>} - { document, holdFunds }
 */
export const createCashout = async (userId, amountRequested, bankInfo = {}, adminFeePercentage) => {
  if (!userId) throw new Error('Missing userId');
  const numericAmount = Number(amountRequested);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) throw new Error('Invalid amount');

  // Determine fee based on cooldown
  let feePct;
  if (typeof adminFeePercentage === 'number') {
    feePct = adminFeePercentage > 1 ? adminFeePercentage / 100 : adminFeePercentage;
  } else {
    const lastCashout = await getLastCashout(userId);
    feePct = calculateCooldownFee(lastCashout ? lastCashout.$createdAt : null);
  }

  // Calculate final payout amount (rounded to 2 decimals)
  const feeAmount = Math.round((numericAmount * feePct) * 100) / 100;
  let finalPayout = Math.round((numericAmount - feeAmount) * 100) / 100;

  // If net amount is less than 0.01, auto-reject
  let status = 'pending';
  if (finalPayout < 0.01) {
    finalPayout = 0;
    status = 'rejected';
    console.warn('Auto-rejecting cashout: net payout below minimum after fees.');
  }

  // Compute scheduled payout date - Collection day is Sunday
  const now = new Date();
  const daysUntilSunday = 7 - now.getDay(); // Sunday is 0, so if today is Sunday, adds 7 days
  const scheduledPayoutDate = new Date(now.getTime() + daysUntilSunday * 24 * 60 * 60 * 1000).toISOString();


  const payload = {
    user_id: userId,
    amount_requested: numericAmount,
    payout_tier: '1_week',
    // Store as string because Appwrite collection defines this attribute as string

    admin_fee_percentage: String(feePct),
    final_payout_amount: finalPayout,
    bank_name: bankInfo.bank_name || '',
    account_number: bankInfo.account_number || '',
    account_holder_name: bankInfo.account_holder_name || '',
    status: status, // pending | completed | rejected
    scheduled_payout_date: scheduledPayoutDate
  };
  try {
    const doc = await databases.createDocument(
      DATABASE_ID,
      CASHOUT_COLLECTION_ID,
      ID.unique(),
      payload,
      [
        Permission.read(Role.user(userId)),
        Permission.update(Role.user(userId))
      ]
    );

    // Return doc and a flag whether funds should be put on hold (only when status is pending)
    return { document: doc, holdFunds: status === 'pending' };
  } catch (error) {
    console.error('createCashout error', error);
    const errMsg = error?.message || String(error);
    // Detect unknown attribute errors returned from Appwrite and provide helpful guidance
    const unknownMatch = errMsg.match(/Unknown attribute: "?([^"]+)"?/i);
    if (unknownMatch && unknownMatch[1]) {
      const attr = unknownMatch[1];
      throw new Error(`createCashout failed: Unknown attribute "${attr}" on cashout collection. Please add this attribute to your Appwrite collection or remove it from the payload. Payload keys: ${Object.keys(payload).join(', ')}`);
    }
    throw new Error('createCashout failed: ' + errMsg);
  }
};

/**
 * List cashout requests for a given user
 * @param {string} userId
 * @returns {Promise<Array>} documents
 */
export const listCashoutsByUser = async (userId) => {
  if (!userId) return [];
  try {
    const res = await databases.listDocuments(DATABASE_ID, CASHOUT_COLLECTION_ID, [
      Query.equal('user_id', userId),
      Query.orderDesc('$createdAt')
    ]);
    return res.documents || [];
  } catch (error) {
    console.error('listCashoutsByUser error', error);
    throw error;
  }
};

/**
 * Get a single cashout by document id
 */
export const getCashoutById = async (documentId) => {
  try {
    const doc = await databases.getDocument(DATABASE_ID, CASHOUT_COLLECTION_ID, documentId);
    return doc;
  } catch (error) {
    console.error('getCashoutById error', error);
    throw error;
  }
};
